import { useState, useEffect } from 'react';
import {
  Table,
  ScrollArea,
  UnstyledButton,
  Group,
  Text,
  Center,
  TextInput,
  createStyles,
  Modal,
  rem,
  Button,
} from '@mantine/core';
import { IconSelector, IconChevronDown, IconChevronUp, IconSearch, IconTrash, IconEditCircle, IconUserCog } from '@tabler/icons-react';
import usePagination from '../hooks/usePagination';
import Paginator from './Paginator';
import { useDisclosure } from '@mantine/hooks';
import ContainerSubmissionEditForm from './forms/ContainerSubmissionEditForm';
import { useNavigate } from 'react-router-dom';
import moment from 'moment/moment';

const useStyles = createStyles((theme) => ({
  th: {
    padding: 0
  },
  control: {
    width: '100%',
    padding: 'var(--mantine-spacing-xs) var(--mantine-spacing-md)',
    '&hover': {
      backgroundColor: 'light-dark(var(--mantine-color-gray-0), var(--mantine-color-dark-6));'
    }
  },
  icon: {
    width: rem(21),
    height: rem(21),
    borderRadius: rem(21)
  },
}));

const tableKeys = [
  'companyName',
  'companyRole',
  'isOBRCMember',
  'firstName',
  'lastName',
  'title',
];

function Th({ children, reversed, sorted, onSort }) {
  const { classes } = useStyles();
  const Icon = sorted ? (reversed ? IconChevronUp : IconChevronDown) : IconSelector;
  return (
    <th className={classes.th}>
      <UnstyledButton onClick={onSort} className={classes.control}>
        <Group justify="space-between" noWrap>
          <Text fw={500} fz="sm">
            {children}
          </Text>
          <Center className={classes.icon}>
            <Icon style={{ width: rem(16), height: rem(16) }} stroke={1.5} />
          </Center>
        </Group>
      </UnstyledButton>
    </th>
  );
}

function filterData(data, search) {
  const query = search.toString().toLowerCase().trim();
  return data.filter((item) =>
    tableKeys.some((key) => item[key].toString().toLowerCase().includes(query))
  );
}

function sortData(data, payload) {
  const { sortBy } = payload;

  if (!sortBy) {
    return filterData(data, payload.search);
  }

  return filterData(
    [...data].sort((a, b) => {
      if (payload.reversed) {
        return b[sortBy].toString().localeCompare(a[sortBy]);
      }

      return a[sortBy].toString().localeCompare(b[sortBy]);
    }),
    payload.search
  );
}

export const UsersTable = ({ tableData, maxRows = 10 }) => {
  const [search, setSearch] = useState('');
  const [sortedData, setSortedData] = useState(tableData);
  const [sortBy, setSortBy] = useState(null);
  const [reverseSortDirection, setReverseSortDirection] = useState(false);
  const [activePage, pageCount, currentItems, onPageClick, setPageData] = usePagination(tableData, maxRows);
  const navigate = useNavigate();

  useEffect(() => {
    setPageData(sortedData);
  }, [sortedData]);

  useEffect(() => {
    setSortedData(tableData);
  }, [tableData])

  const setSorting = (field) => {
    const reversed = field === sortBy ? !reverseSortDirection : false;
    setReverseSortDirection(reversed);
    setSortBy(field);
    setSortedData(sortData(tableData, { sortBy: field, reversed, search }));
  };

  const handleSearchChange = (event) => {
    const { value } = event.currentTarget;
    setSearch(value.toString());
    setSortedData(sortData(tableData, { sortBy, reversed: reverseSortDirection, search: value.toString() }));
  };

  const rows = (currentItems || []).map((row, idx) => (
    <tr key={idx}>
      <td>{row.companyName}</td>
      <td>{row.companyRole}</td>
      <td>{row.isOBRCMember.toString()}</td>
      <td>{row.firstName}</td>
      <td>{row.lastName}</td>
          <td>{row.title}</td>
      
          {row.createdDate != null ?
              (<td>{moment(row.createdDate).format('YYYY-MM-DD')}</td>)
        :
        (<td>&nbsp;</td>)
          }
        
      <td>
        <Group grow spacing='xs'>
          <Button compact color='dark' onClick={() => navigate(`/admin/users/${row.id}`)}><IconUserCog size={20} /></Button>
        </Group>
      </td>
    </tr>
  ));

  return (
    <ScrollArea>
      <TextInput
        placeholder="Search by any field"
        mb="md"
        icon={<IconSearch style={{ width: rem(16), height: rem(16) }} stroke={1.5} />}
        value={search}
        onChange={handleSearchChange}
      />
      <Table verticalSpacing="xs" layout="fixed" striped highlightOnHover withColumnBorders withBorder>
        <thead>
          <tr>
            <Th
              sorted={sortBy === 'companyName'}
              reversed={reverseSortDirection}
              onSort={() => setSorting('companyName')}
            >
              Company
            </Th>
            <Th
              sorted={sortBy === 'companyRole'}
              reversed={reverseSortDirection}
              onSort={() => setSorting('companyRole')}
            >
              Role
            </Th>
            <Th
              sorted={sortBy === 'isOBRCMember'}
              reversed={reverseSortDirection}
              onSort={() => setSorting('isOBRCMember')}
            >
              OBRC Member?
            </Th>
            <Th
              sorted={sortBy === 'firstName'}
              reversed={reverseSortDirection}
              onSort={() => setSorting('firstName')}
            >
              First Name
            </Th>
            <Th
              sorted={sortBy === 'lastName'}
              reversed={reverseSortDirection}
              onSort={() => setSorting('lastName')}
            >
              Last Name
            </Th>
            <Th
              sorted={sortBy === 'title'}
              reversed={reverseSortDirection}
              onSort={() => setSorting('title')}
            >
              Title
            </Th>
            <Th
                sorted={sortBy === 'createdDate'}
                reversed={reverseSortDirection}
                onSort={() => setSorting('createdDate')}
            >
                Created Date
            </Th>
            <Th>Actions</Th>
          </tr>
        </thead>
        <tbody>
          {rows.length > 0 ? (
            rows
          ) : (
            <tr>
              <td colSpan={tableKeys.length}>
                <Text fw={500} fz={20} ta="center" p={25}>
                  ...Nothing Found...
                </Text>
              </td>
            </tr>
          )}
        </tbody>
      </Table>
      <Paginator
        onPageClick={onPageClick}
        pageCount={pageCount}
        pageRangeDisplayed={5}
        marginPagesDisplayed={3}
        activePage={activePage}
      />
    </ScrollArea>
  );
}